import { Suspense, useMemo } from "react";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { AnalyticsSection } from "@/modules/analytics/components/AnalyticsSection";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import type { DateRangeOption } from "../../util";
import { CommunicationsCard } from "./components/CommunicationsCard";
import { EngagementCard } from "./components/EngagementCard";
import { FeelingAtHomeCard } from "./components/FeelingAtHomeCard";
import { OneOnOneCommunicationsCard } from "./components/OneOnOneCommunicationsCard";
import { QuestionsAskedAndSolvedCard } from "./components/QuestionsAskedAndSolvedCard";
import { ReachAdoptionCard, ReachAdoptionCompanyCard } from "./components/ReachAdoptionCard";
import { SocialEnvironmentalResidentActivityCard } from "./components/SocialEnvironmentalResidentActivityCard";
import { SocialInvolvementCard } from "./components/SocialInvolvementCard";
import { TicketClosingTimeServiceLevelsCard } from "./components/TicketClosingTimeServiceLevelsCard";
import { TicketHandlingTimeCard } from "./components/TicketHandlingTimeCard";
import { TicketOverviewCard } from "./components/TicketOverviewCard";
import { TicketSatisfactionCard } from "./components/TicketSatisfactionCard";

export function AnalyticsOverviewPage(): React.ReactNode {
  return (
    <Suspense fallback={<FullSizeLoader withPadding />}>
      <PageContent />
    </Suspense>
  );
}

/**
 * Content in a separate component so that useScrollToHash runs after the content is loaded.
 */
function PageContent() {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const projectId = useProjectId();

  useScrollToHash();

  const dateRangeOptions = useMemo<NonEmptyArray<DateRangeOption>>(
    () => [
      {
        name: t("common.date-range.last-months", { count: 6 }),
        value: "last6Months",
      },
      {
        name: t("common.date-range.last-year"),
        value: "lastYear",
      },
    ],
    [t],
  );

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.analytics-overview.overview.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.analytics-overview.overview.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: [projectId] }}
          context={{
            scope: "project",
            entity: "analytics",
            page: "detail",
          }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics.suggestion.2"),
          ]}
        />
      }
    >
      <PageGrid.Row>
        {sessionUser.project.type !== "companyBased" && (
          <PageGrid.Column size="100%">
            <AnalyticsSection
              id="fah-section"
              title={t("page.analytics-overview.overview.sentiment.title")}
              description={t("page.analytics-overview.overview.note")}
            >
              <FeelingAtHomeCard />
            </AnalyticsSection>
          </PageGrid.Column>
        )}

        <PageGrid.Column size="100%">
          <AnalyticsSection
            id="adoption-section"
            isFullWidth
            title={t("page.analytics-overview.overview.platform-adoption.title")}
          >
            {sessionUser.project.type === "companyBased" ? <ReachAdoptionCompanyCard /> : <ReachAdoptionCard />}
          </AnalyticsSection>
        </PageGrid.Column>

        <PageGrid.Column size="100%">
          <AnalyticsSection id="esg-section" title={t("page.analytics-overview.overview.esg.title")}>
            <EngagementCard />
            <SocialInvolvementCard />
            <CommunicationsCard />
            <OneOnOneCommunicationsCard dateRangeOptions={dateRangeOptions} />
            <SocialEnvironmentalResidentActivityCard dateRangeOptions={dateRangeOptions} />
            <QuestionsAskedAndSolvedCard dateRangeOptions={dateRangeOptions} />
          </AnalyticsSection>
        </PageGrid.Column>

        <PageGrid.Column size="100%">
          <AnalyticsSection id="tickets-section" title={t("page.analytics-overview.overview.tickets.title")}>
            <Stack gap="lg">
              <TicketOverviewCard />
              <TicketSatisfactionCard dateRangeOptions={dateRangeOptions} />
            </Stack>
            <Stack gap="lg">
              <TicketClosingTimeServiceLevelsCard />
              <TicketHandlingTimeCard dateRangeOptions={dateRangeOptions} />
            </Stack>
          </AnalyticsSection>
        </PageGrid.Column>
      </PageGrid.Row>
    </DocumentPage>
  );
}
